import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
    BannerSmallSection as BannerSmallSectionDS,
    ModalSmall,
    ModalRef,
} from '@solublestudio/legalbrave-design-system';
import { updateProfile } from 'api-base/lib/utils/profile';
import { useToastContext } from '../../base-components/Toast/ToastContext';
import { navigate } from 'Link';
import { useTranslate } from '../../hooks/useTranslate';
import { useUser } from 'api-base/auth';
import { UserAuth0, UserPlan } from 'api-base/lib/types/User';

const UPGRADE_API_PATH = '/api/protected/upgrade-plan/';

const BannerSmallSection = ({ cta, ...props }: any) => {
    const { isLoading, ...session } = useUser();
    const user = session.user as unknown as UserAuth0;
    const [t] = useTranslate();

    const difference = useMemo(() => user?.profile?.plan === UserPlan.ESSENTIAL_SEMESTRAL ? '$75' : '$15', [user]);
    const newPrice = useMemo(() => user?.profile?.plan === UserPlan.ESSENTIAL_SEMESTRAL ? '$199.95' : '$39.99', [user]);
    const modalDescription = useMemo(() => t('modal.upgrade.description', { difference, newPrice }), [difference, newPrice]);

    const ref = useRef<ModalRef>(null);

    const loadingModalOptions = {
        iconName: 'loading',
        description: t('form.subscription.loading'),
        iconType: 'loading',
        hideButtons: true,
    };

    const [modalOptions, setModalOptions] = useState<any>({
        title: t('modal.upgrade.title'),
        description: modalDescription,
    });

    useEffect(() => {
        setModalOptions({
            title: t('modal.upgrade.title'),
            description: modalDescription,
        })
    }, [modalDescription])
    
    const addToast = useToastContext();

    const isUpgrade = cta && cta.href === UPGRADE_API_PATH;
    let ctaAdapted = useMemo(() => {
        if (isUpgrade) {
            return {
                ...cta,
                onClick: (ev: Event) => {
                    ev?.preventDefault();
                    ref?.current?.toggle();
                },
            };
        } else {
            return cta;
        }
    }, [cta]);

    const upgradeFn = useCallback((ev: Event) => {
        ev?.preventDefault();
        ev?.stopPropagation();
        setModalOptions(loadingModalOptions);
        fetch(UPGRADE_API_PATH, {
            method: 'POST',
        })
            .then(async (req) => {
                if (req.status === 200) {
                    ref?.current?.toggle();
                    addToast({
                        type: 'success',
                        message: t('toast.upgrade.success'),
                    });
                    await updateProfile();
                    navigate('/user/home');
                } else {
                    console.error(req.body);
                    ref?.current?.toggle();
                    addToast({ type: 'error', message: t('toast.upgrade.error') }, 12000);
                }
            })
            .catch((error) => {
                console.error(error);
                ref?.current?.toggle();
                addToast({ type: 'error', message: t('toast.upgrade.error') });
            });
    }, []);
    return (
        <>
            <BannerSmallSectionDS {...props} cta={ctaAdapted} />
            {isUpgrade && (
                <ModalSmall
                    ref={ref}
                    {...modalOptions}
                    buttons={[
                        {
                            label: t('modal.upgrade.cancel'),
                            variant: 'secondaryLarge',
                            onClick: (ev: Event) => {
                                ev?.preventDefault();
                                ev?.stopPropagation();
                                ref?.current?.toggle();
                            },
                        },
                        {
                            label: t('modal.upgrade.cta'),
                            variant: 'primaryLarge',
                            onClick: upgradeFn,
                        },
                    ]}
                />
            )}
        </>
    );
};

export default BannerSmallSection;
